import React, { useState, useEffect, useRef, memo } from 'react';
import { motion } from 'motion/react';
import { Image } from '@phosphor-icons/react';
import api from '../services/api';
import {
  getItemCategory,
  getItemEntityNames,
  getItemHeadline,
  getItemMusicLinks,
  getItemOtherMusicLinks,
  getItemRawResponse,
  getItemSummary,
  getScoreLabel,
} from '../utils/itemHelpers';
import { cardAccessibleName } from '../utils/accessibleName';
import MusicLinks from './MusicLinks';
import { useMusicLinks } from '../utils/musicService';

const MAX_ENTITY_CHIPS = 3;

// "Moment" reveal variants (COL-92) for the headline/badge/entity-chips that
// appear the instant analysis completes. Each has its own small stagger delay
// so the badge, headline, and chips land in a quick cascade rather than all
// at once. Only ever applied via `initial="hidden"` on a card's very first
// mount (see `shouldReveal` below) — Motion reads `initial` once, at mount,
// and ignores it on every later re-render, which is what makes this fire
// once per genuine transition and never again (not on remount — there isn't
// one for an existing instance — not on refetch, not on scroll-into-view).
//
// Deliberately transform-only (scale/y), never opacity: the root
// `<MotionConfig reducedMotion="user">` in main.jsx strips transform/layout
// animations for reduced-motion users project-wide (frontend/CLAUDE.md), so
// keeping this reveal to transform values alone means it degrades to an
// instant, non-animated state change purely through that single existing
// mechanism — no per-component `useReducedMotion()` branch needed here.
const badgeRevealVariants = {
  hidden: { scale: 0.85 },
  visible: { scale: 1, transition: { duration: 0.28, ease: 'easeOut' } },
};
const headlineRevealVariants = {
  hidden: { y: 6 },
  visible: { y: 0, transition: { duration: 0.28, ease: 'easeOut', delay: 0.08 } },
};
const chipsRevealVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } },
};
const chipRevealVariants = {
  hidden: { y: 4, scale: 0.9 },
  visible: { y: 0, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } },
};

// Memoized to prevent re-renders when parent state changes
const ItemCard = memo(function ItemCard({ item, onClick, index, processingState, justCompleted = false, onRevealed }) {
  // Prefer `image` (the processed ≤2MB JPEG, not the original) — the 200px
  // thumbnail is visibly blurry at card size on retina screens. Thumbnails
  // remain the fallback for responses that don't carry `image`.
  const [imageUrl, setImageUrl] = useState(item.image || item.thumbnail_url || item.thumbnail || null);
  const [imageError, setImageError] = useState(false);

  // Get image URL if not provided (with cleanup to prevent memory leaks)
  useEffect(() => {
    let isMounted = true;

    async function loadImage() {
      if (imageUrl || imageError) return;

      // Skip fetching if item has no stored image (all image fields null)
      // This avoids 404s for items whose source file was never persisted
      if (!item.image && !item.thumbnail && !item.thumbnail_url) {
        if (isMounted) setImageError(true);
        return;
      }

      try {
        const filename = item.filename || item.original_s3_key?.split('/').pop();
        if (filename) {
          const { url } = await api.getImageUrl(filename);
          if (isMounted) setImageUrl(url);
        }
      } catch {
        if (isMounted) setImageError(true);
      }
    }

    loadImage();

    return () => { isMounted = false; };
  }, [item, imageUrl, imageError]);

  // Use shared helpers to extract fields from item (handles both nested and flat structures)
  const category = (getItemCategory(item) || 'uncategorized').toLowerCase();
  const headline = getItemHeadline(item);
  const summary = getItemSummary(item);
  const rawResponse = getItemRawResponse(item);
  const source = rawResponse.media_metadata?.source_title ||
                 rawResponse.media_metadata?.platform ||
                 '';
  const date = item.created_at ? formatDate(item.created_at) : '';
  const entityNames = getItemEntityNames(item);
  const musicLinks = useMusicLinks({
    ...getItemMusicLinks(item),
    otherLinks: getItemOtherMusicLinks(item),
  });
  const visibleEntityNames = entityNames.slice(0, MAX_ENTITY_CHIPS);
  const overflowEntityCount = entityNames.length - visibleEntityNames.length;

  const isProcessing = processingState?.isProcessing;

  // Analysis reveal on completion (COL-92). `justCompleted` is only ever true
  // for the one render where the caller (Gallery) has just observed this
  // specific item transition from processing -> complete; it's read once,
  // here, and never re-derived from a later prop change. Reduced-motion
  // degrade is handled entirely by the root MotionConfig (see the variants
  // above), not by branching here.
  const shouldReveal = justCompleted;
  const hasNotifiedRevealRef = useRef(false);
  useEffect(() => {
    if (justCompleted && !hasNotifiedRevealRef.current) {
      hasNotifiedRevealRef.current = true;
      onRevealed?.();
    }
    // Intentionally empty deps: this must run exactly once, at mount, using
    // the `justCompleted` value the card was born with — not on every
    // re-render caused by unrelated prop/state changes (a poll tick,
    // scrolling the card back into view, a cache refetch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Accessible name states what the card IS (headline + category), not "card"
  // or "button" — a screen reader user tabbing through a grid of these needs
  // to tell them apart by name alone. "uncategorized" is a placeholder, not
  // real information, so it's dropped rather than announced.
  const accessibleName = cardAccessibleName(headline, category, { omitType: 'uncategorized' });

  const cardContent = (
    <>
      <div className="item-card-image">
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={headline}
            loading="lazy"
            onError={() => setImageError(true)}
            style={isProcessing ? { opacity: 0.7 } : undefined}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-sunken)',
          }}>
            <ImagePlaceholderIcon />
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <ProcessingOverlay
            phase={processingState.currentPhase}
            progress={processingState.progress || 0}
            isTimedOut={processingState.isTimedOut}
            onRetry={processingState.onRetry}
          />
        )}

        {!isProcessing && (
          <motion.span
            className="item-card-badge"
            initial={shouldReveal ? 'hidden' : false}
            animate="visible"
            variants={badgeRevealVariants}
          >
            {category}
          </motion.span>
        )}
      </div>

      <div className="item-card-content">
        <motion.h3
          className="item-card-title"
          initial={shouldReveal ? 'hidden' : false}
          animate="visible"
          variants={headlineRevealVariants}
        >
          {isProcessing ? (item.filename || 'Processing...') : headline}
        </motion.h3>
        {!isProcessing && source && <div className="item-card-source">{source}</div>}
        {/* 2-line clamp (COL-95, CSS-only via .item-card-description). The
            card's accessible name comes from the overlay button's explicit
            aria-label (headline + category), not from this text, so visually
            clamping/truncating it never changes what a screen reader
            announces for the card. */}
        {!isProcessing && summary && (
          <p className="item-card-description">{summary}</p>
        )}
        {/* Entity chips (COL-92) — sourced from `item.enrichments`, already
            batch-attached by the backend for both /items and /items/{id}, so
            this never triggers a per-card fetch. */}
        {!isProcessing && visibleEntityNames.length > 0 && (
          <motion.div
            className="item-card-entity-chips"
            initial={shouldReveal ? 'hidden' : false}
            animate="visible"
            variants={chipsRevealVariants}
          >
            {visibleEntityNames.map((name) => (
              <motion.span key={name} className="item-card-entity-chip" variants={chipRevealVariants}>
                {name}
              </motion.span>
            ))}
            {overflowEntityCount > 0 && (
              <motion.span
                className="item-card-entity-chip item-card-entity-chip-overflow"
                variants={chipRevealVariants}
              >
                +{overflowEntityCount}
              </motion.span>
            )}
          </motion.div>
        )}
      </div>

      <div className="item-card-footer">
        <span className="item-card-date">{date}</span>
        <div className="item-card-footer-end">
          {getScoreLabel(item) && (
            <span style={{
              fontSize: '0.75rem',
              color: 'var(--accent)',
              fontWeight: 500,
            }}>
              {getScoreLabel(item)}
            </span>
          )}
          {!isProcessing && musicLinks.length > 0 && (
            <div className="item-card-music-links">
              <MusicLinks links={musicLinks} classPrefix="item-card" stopPropagation />
            </div>
          )}
        </div>
      </div>
    </>
  );

  // The root element stays a single, stable <motion.article> across the
  // processing -> done transition — switching host tags (article <-> button)
  // there would force React to unmount/remount the whole card (losing the
  // image, layout state and any in-flight animation) at exactly the moment
  // ProcessingItemCard is trying hardest to avoid a flicker (COL-91 review).
  //
  // Keyboard/click affordance instead comes from a full-card overlay
  // <button>, only rendered while the card is actually actionable. Because
  // the "Check now" retry button (inside ProcessingOverlay) only exists while
  // processing, and this overlay button only exists while NOT processing,
  // the two are never both present — so the retry button is never nested
  // inside another button.
  return (
    <motion.article
      className="item-card"
      layout="position"
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
      }}
      exit={{ opacity: 0, transition: { duration: 0.12 } }}
      whileHover={isProcessing ? undefined : { y: -4 }}
      transition={{ duration: 0.15 }}
      style={isProcessing ? { cursor: 'default' } : undefined}
    >
      {!isProcessing && (
        <button
          type="button"
          className="item-card-link-overlay"
          onClick={onClick}
          aria-label={accessibleName}
        />
      )}
      {cardContent}
    </motion.article>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders from inline onClick functions
  const prevId = prevProps.item?.item_id || prevProps.item?.id;
  const nextId = nextProps.item?.item_id || nextProps.item?.id;
  return (
    prevId === nextId &&
    prevProps.index === nextProps.index &&
    prevProps.item?.created_at === nextProps.item?.created_at &&
    prevProps.item?.image === nextProps.item?.image &&
    prevProps.item?.thumbnail === nextProps.item?.thumbnail &&
    prevProps.item?.thumbnail_url === nextProps.item?.thumbnail_url &&
    prevProps.processingState?.isProcessing === nextProps.processingState?.isProcessing &&
    prevProps.processingState?.progress === nextProps.processingState?.progress &&
    prevProps.processingState?.currentPhase === nextProps.processingState?.currentPhase &&
    prevProps.processingState?.isTimedOut === nextProps.processingState?.isTimedOut
  );
});

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ProcessingOverlay({ phase, progress, isTimedOut, onRetry }) {
  if (isTimedOut) {
    return (
      <div className="item-card-processing-overlay">
        <span className="item-card-processing-phase">
          Still processing. Check back later.
        </span>
        <button
          type="button"
          className="btn btn-secondary btn-sm item-card-retry-button"
          onClick={(e) => {
            e.stopPropagation();
            onRetry?.();
          }}
        >
          Check now
        </button>
      </div>
    );
  }

  return (
    <div className="item-card-processing-overlay">
      <div className="loading-spinner" style={{ width: 24, height: 24 }} />
      <span className="item-card-processing-phase">
        {phase || 'Processing...'}
      </span>
      <div className="item-card-progress-track">
        <div
          className="item-card-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="item-card-progress-text">{progress}%</span>
    </div>
  );
}

function ImagePlaceholderIcon() {
  return <Image size={48} />;
}

export default ItemCard;
